import { useEffect, useMemo, useState } from 'react';
import type {
  Concept,
  ConceptCategory,
  ConceptDraft,
  ConceptPatch,
  ConceptRegulatory,
  ConceptRevision,
  ConceptStatus,
} from '@sr-test-wizard/shared';
import { ontologyApi } from '../../lib/ontologyApi';
import { formatAppDateTime } from '../../lib/localTime';

const CATEGORIES: ConceptCategory[] = ['mechanic', 'ui-element', 'menu-type', 'math-notion', 'meta'];
const STATUSES: ConceptStatus[] = ['active', 'draft', 'deprecated'];

interface FormState {
  slug: string;
  canonicalName: string;
  category: ConceptCategory;
  status: ConceptStatus;
  taggable: boolean;
  jiraIndicatorKey: string;
  definition: string;
  invariant: string;
  aliasesText: string;
  regulatory: ConceptRegulatory[];
  reason: string;
}

function emptyForm(): FormState {
  return {
    slug: '',
    canonicalName: '',
    category: 'mechanic',
    status: 'active',
    taggable: false,
    jiraIndicatorKey: '',
    definition: '',
    invariant: '',
    aliasesText: '',
    regulatory: [],
    reason: '',
  };
}

function conceptToForm(c: Concept): FormState {
  return {
    slug: c.slug,
    canonicalName: c.canonicalName,
    category: c.category,
    status: c.status,
    taggable: c.taggable,
    jiraIndicatorKey: c.jiraIndicatorKey ?? '',
    definition: c.definition,
    invariant: c.invariant,
    aliasesText: c.aliases.filter((a) => a.kind === 'generic').map((a) => a.text).join('\n'),
    regulatory: c.regulatoryRelevance,
    reason: '',
  };
}

function genericAliases(text: string) {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((t) => ({ text: t, kind: 'generic' as const, studio: null }));
}

function formToDraft(f: FormState): ConceptDraft {
  return {
    slug: f.slug.trim(),
    canonicalName: f.canonicalName.trim(),
    category: f.category,
    status: f.status,
    taggable: f.taggable,
    jiraIndicatorKey: f.jiraIndicatorKey.trim() || null,
    definition: f.definition.trim(),
    invariant: f.invariant.trim(),
    aliases: genericAliases(f.aliasesText),
    signals: [],
    relations: [],
    examples: [],
    regulatoryRelevance: f.regulatory.filter((r) => r.jurisdiction.trim()),
  };
}

/** For edits we only send managed fields; signals/relations/examples are preserved server-side. Branded aliases are merged back so they aren't lost. */
function formToPatch(f: FormState, existing: Concept): ConceptPatch {
  const branded = existing.aliases.filter((a) => a.kind === 'branding');
  return {
    slug: f.slug.trim(),
    canonicalName: f.canonicalName.trim(),
    category: f.category,
    status: f.status,
    taggable: f.taggable,
    jiraIndicatorKey: f.jiraIndicatorKey.trim() || null,
    definition: f.definition.trim(),
    invariant: f.invariant.trim(),
    aliases: [...genericAliases(f.aliasesText), ...branded],
    regulatoryRelevance: f.regulatory.filter((r) => r.jurisdiction.trim()),
  };
}

function RegulatoryEditor({
  rows,
  onChange,
}: {
  rows: ConceptRegulatory[];
  onChange: (next: ConceptRegulatory[]) => void;
}) {
  return (
    <div className="flex-col" style={{ gap: 6 }}>
      {rows.map((r, i) => (
        <div className="row" key={i} style={{ gap: 6 }}>
          <input
            placeholder="Jurisdiction (e.g. NL)"
            value={r.jurisdiction}
            style={{ width: 130 }}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...r, jurisdiction: e.target.value };
              onChange(next);
            }}
          />
          <select
            value={r.rule}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...r, rule: e.target.value as ConceptRegulatory['rule'] };
              onChange(next);
            }}
          >
            <option value="forbidden">forbidden</option>
            <option value="required">required</option>
            <option value="conditional">conditional</option>
          </select>
          <input
            placeholder="REQ key (pointer, optional)"
            value={r.reqKey ?? ''}
            style={{ flex: 1 }}
            onChange={(e) => {
              const next = [...rows];
              next[i] = { ...r, reqKey: e.target.value || null };
              onChange(next);
            }}
          />
          <button type="button" className="btn small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn small btn-ghost"
        onClick={() => onChange([...rows, { jurisdiction: '', reqKey: null, rule: 'forbidden' }])}
      >
        + Add jurisdiction rule (pointer only - rule text stays in Jira)
      </button>
    </div>
  );
}

function ConceptForm({
  form,
  busy,
  error,
  isEdit,
  onChange,
  onCancel,
  onSave,
}: {
  form: FormState;
  busy: boolean;
  error: string | null;
  isEdit: boolean;
  onChange: (f: FormState) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const needsReason = form.regulatory.some((r) => r.jurisdiction.trim());
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="flex-col" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 8 }}>
          <input
            placeholder="slug (e.g. buy_feature)"
            value={form.slug}
            style={{ width: 200 }}
            disabled={busy}
            onChange={(e) => onChange({ ...form, slug: e.target.value })}
          />
          <input
            placeholder="Canonical name"
            value={form.canonicalName}
            style={{ flex: 1 }}
            disabled={busy}
            onChange={(e) => onChange({ ...form, canonicalName: e.target.value })}
          />
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select value={form.category} disabled={busy} onChange={(e) => onChange({ ...form, category: e.target.value as ConceptCategory })}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={form.status} disabled={busy} onChange={(e) => onChange({ ...form, status: e.target.value as ConceptStatus })}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <label className="row small" style={{ gap: 4 }}>
            <input type="checkbox" checked={form.taggable} disabled={busy} onChange={(e) => onChange({ ...form, taggable: e.target.checked })} />
            taggable (in evidence tagger)
          </label>
          <input
            placeholder="Jira indicator key (e.g. gp-gamble)"
            value={form.jiraIndicatorKey}
            style={{ width: 200 }}
            disabled={busy}
            onChange={(e) => onChange({ ...form, jiraIndicatorKey: e.target.value })}
          />
        </div>
        <label className="muted small">Invariant (what it DOES - load-bearing)</label>
        <textarea
          value={form.invariant}
          rows={2}
          disabled={busy}
          placeholder="Pay a premium to enter the bonus immediately, bypassing natural triggering."
          onChange={(e) => onChange({ ...form, invariant: e.target.value })}
        />
        <label className="muted small">Definition (human-facing prose)</label>
        <textarea
          value={form.definition}
          rows={3}
          disabled={busy}
          onChange={(e) => onChange({ ...form, definition: e.target.value })}
        />
        <label className="muted small">Aliases (one per line)</label>
        <textarea
          value={form.aliasesText}
          rows={3}
          disabled={busy}
          placeholder={'bonus buy\nbuy bonus\nfeature buy'}
          onChange={(e) => onChange({ ...form, aliasesText: e.target.value })}
        />
        <label className="muted small">Regulatory relevance (pointers)</label>
        <RegulatoryEditor rows={form.regulatory} onChange={(regulatory) => onChange({ ...form, regulatory })} />
        {needsReason && (
          <>
            <label className="muted small">Reason (required for regulatory edits - recorded in audit log)</label>
            <input value={form.reason} disabled={busy} onChange={(e) => onChange({ ...form, reason: e.target.value })} />
          </>
        )}
        {error && <div className="muted small" style={{ color: 'var(--bad)' }}>{error}</div>}
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn small btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn small btn-primary"
            onClick={onSave}
            disabled={busy || !form.slug.trim() || !form.canonicalName.trim() || (needsReason && !form.reason.trim())}
          >
            {busy ? 'Saving…' : isEdit ? 'Save concept' : 'Create concept'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OntologyPage() {
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(emptyForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm());
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [revisionsFor, setRevisionsFor] = useState<{ concept: Concept; rows: ConceptRevision[] } | null>(null);

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setConcepts(await ontologyApi.list());
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return concepts;
    return concepts.filter(
      (c) =>
        c.canonicalName.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        c.aliases.some((a) => a.text.toLowerCase().includes(q)),
    );
  }, [concepts, filter]);

  const startAdd = () => {
    setAdding(true);
    setAddForm(emptyForm());
    setFormError(null);
    setEditingId(null);
  };

  const submitAdd = async () => {
    setBusy(true);
    setFormError(null);
    try {
      const created = await ontologyApi.create(formToDraft(addForm), { editor: 'web', reason: addForm.reason });
      setConcepts((p) => [...p, created].sort((a, b) => a.canonicalName.localeCompare(b.canonicalName)));
      setAdding(false);
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c: Concept) => {
    setEditingId(c.id);
    setEditForm(conceptToForm(c));
    setFormError(null);
    setAdding(false);
  };

  const submitEdit = async (existing: Concept) => {
    setBusy(true);
    setFormError(null);
    try {
      const updated = await ontologyApi.update(existing.id, formToPatch(editForm, existing), {
        editor: 'web',
        reason: editForm.reason,
      });
      setConcepts((p) => p.map((c) => (c.id === existing.id ? updated : c)));
      setEditingId(null);
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (c: Concept) => {
    if (!window.confirm(`Delete concept "${c.canonicalName}"? (kept in the revision audit log)`)) return;
    try {
      await ontologyApi.remove(c.id);
      setConcepts((p) => p.filter((x) => x.id !== c.id));
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };

  const openRevisions = async (c: Concept) => {
    try {
      const rows = await ontologyApi.revisions(c.id);
      setRevisionsFor({ concept: c, rows });
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Ontology</h1>
        <span className="spacer" />
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" style={{ width: 220 }} />
        {!adding && (
          <button type="button" className="btn small btn-primary" onClick={startAdd}>+ Add concept</button>
        )}
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        The structured slot-concept catalogue that replaced the flat glossary. Each concept carries a functional
        invariant, aliases/brandings, signals and near-neighbour disambiguation. The AI surfaces inject only the
        relevant concept cards per test (so prompts stay small), and the evidence tagger is a view over the
        <strong> taggable</strong> concepts. Edits are recorded in an append-only revision log.
      </p>

      {loadError && <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>{loadError}</div>}

      {adding && (
        <ConceptForm
          form={addForm}
          busy={busy}
          error={formError}
          isEdit={false}
          onChange={setAddForm}
          onCancel={() => setAdding(false)}
          onSave={() => void submitAdd()}
        />
      )}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <div className="flex-col" style={{ gap: 8 }}>
          {filtered.map((c) =>
            editingId === c.id ? (
              <ConceptForm
                key={c.id}
                form={editForm}
                busy={busy}
                error={formError}
                isEdit
                onChange={setEditForm}
                onCancel={() => setEditingId(null)}
                onSave={() => void submitEdit(c)}
              />
            ) : (
              <div className="card" key={c.id} style={{ padding: 12 }}>
                <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 15 }}>{c.canonicalName}</strong>
                  <span className="badge small mono">{c.slug}</span>
                  <span className="badge small">{c.category}</span>
                  {c.taggable && <span className="badge small good">taggable</span>}
                  {c.status !== 'active' && <span className="badge small">{c.status}</span>}
                  {c.jiraIndicatorKey && <span className="muted small mono">{c.jiraIndicatorKey}</span>}
                  <span className="spacer" />
                  <button type="button" className="btn small" onClick={() => void openRevisions(c)}>History</button>
                  <button type="button" className="btn small" onClick={() => startEdit(c)}>Edit</button>
                  <button type="button" className="btn small" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }} onClick={() => void removeOne(c)}>Delete</button>
                </div>
                {c.invariant && <div className="small" style={{ marginTop: 6 }}><strong>Invariant:</strong> {c.invariant}</div>}
                {c.aliases.length > 0 && (
                  <div className="muted small" style={{ marginTop: 4 }}>
                    aka {c.aliases.map((a) => a.text).join(', ')}
                  </div>
                )}
                {c.regulatoryRelevance.length > 0 && (
                  <div className="muted small" style={{ marginTop: 4 }}>
                    {c.regulatoryRelevance.map((r) => `${r.jurisdiction}: ${r.rule}${r.reqKey ? ` (${r.reqKey})` : ''}`).join(' · ')}
                  </div>
                )}
              </div>
            ),
          )}
          {filtered.length === 0 && <div className="muted">No concepts match "{filter}".</div>}
        </div>
      )}

      {revisionsFor && (
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Revisions — {revisionsFor.concept.canonicalName}</h3>
            <span className="spacer" />
            <button type="button" className="btn small btn-ghost" onClick={() => setRevisionsFor(null)}>Close</button>
          </div>
          {revisionsFor.rows.length === 0 ? (
            <p className="muted small">No revisions recorded.</p>
          ) : (
            <div className="flex-col" style={{ gap: 6, marginTop: 8 }}>
              {revisionsFor.rows.map((r) => (
                <div key={r.id} className="small" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
                  <span className="badge small">{r.action}</span>{' '}
                  <span className="muted">{formatAppDateTime(r.changedAt)} · {r.editor}</span>
                  {r.reason && <div className="muted">Reason: {r.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
